import { Arg, COMPONENT_STATE_ARGS, Metadata, pick } from '../../../../utils/spds/metadata';
import FlexRow from '../../layout/flex/row.gjs';
import Super from '../../core/base.gjs';
import styles from './base.module.scss';

export const DEFAULT_SIZE = 'normal';
export const ALLOWED_SIZES = [DEFAULT_SIZE, 'small'];

const METADATA = Metadata.define({
  description: `
    Base badge indicator component for SPDS. Badges help quickly identify the status of a task,
    action, or page element. This base component provides shared layout and sizing behavior
    for all badge variants (brand, discovery, info, negative, positive, warning, new, dot, counter).
  `,
  extends: [
    pick(COMPONENT_STATE_ARGS, [
      { name: 'id', description: 'Optional identifier; overrides automated ID generation' },
    ]),
  ],
  meta: {
    size: Arg.oneOf(ALLOWED_SIZES, {
      default: DEFAULT_SIZE,
      description: 'Badge size variant',
    }),
  },
  name: 'Badge',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-Web-Components?m=auto&node-id=23480-130256&t=ADL8oK1s8842xDHJ-1',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/v/0/p/76a020-badge',
  },
});

const baseClass = 'spds-indicator-badge';

export default class SpdsIndicatorBadgeBase extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = baseClass;
  baseStyles = styles;
  classesToMap = ['secondary', 'size'];

  get alignItems() {
    return this.argOrDefault('alignItems') || 'center';
  }

  get gap() {
    return this.argOrDefault('gap') || '12px';
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent') || 'center';
  }

  get size() {
    return this.argOrDefault('size');
  }

  get type() {
    return this.variantClass?.replace(`${baseClass}-`, '') || '';
  }

  <template>
    <FlexRow
      @alignItems={{this.alignItems}}
      @fitContent={{true}}
      @gap={{this.gap}}
      @justifyContent={{this.justifyContent}}
      @tag="span"
      class={{this.classes}}
      data-test-spds-indicator-badge={{this.type}}
      ...attributes
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      {{yield}}
    </FlexRow>
  </template>
}

export const BADGE_BASE_METADATA = SpdsIndicatorBadgeBase.metadata;
